import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-[#800020] py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft size={20} />
            <span className="font-body">Back to Home</span>
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4"
          >
            <Shield className="text-[#D4AF37]" size={40} />
            <h1 className="font-heading text-4xl md:text-5xl text-white">Privacy Policy</h1>
          </motion.div>
          <p className="font-body text-[#FFFDD0]/80 mt-4">Last updated: January 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-[#800020] mb-4">Introduction</h2>
            <p className="font-body text-[#1A1A1A]/80 leading-relaxed">
              Welcome to Devanand Vestment. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or make a purchase from us.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-[#800020] mb-4">Information We Collect</h2>
            <p className="font-body text-[#1A1A1A]/80 leading-relaxed mb-4">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc pl-6 font-body text-[#1A1A1A]/80 space-y-2">
              <li>Name and contact information (email address, phone number)</li>
              <li>Shipping and billing address</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
              <li>Communication preferences</li>
              <li>Any other information you choose to provide via WhatsApp or other contact methods</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-[#800020] mb-4">How We Use Your Information</h2>
            <p className="font-body text-[#1A1A1A]/80 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 font-body text-[#1A1A1A]/80 space-y-2">
              <li>Process and fulfill your orders</li>
              <li>Communicate with you about products, services, and promotions</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Improve our website and customer experience</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-[#800020] mb-4">Information Sharing</h2>
            <p className="font-body text-[#1A1A1A]/80 leading-relaxed">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only with trusted service providers who assist us in operating our website, conducting our business, or servicing you, as long as those parties agree to keep this information confidential.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-[#800020] mb-4">Data Security</h2>
            <p className="font-body text-[#1A1A1A]/80 leading-relaxed">
              We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-[#800020] mb-4">Your Rights</h2>
            <p className="font-body text-[#1A1A1A]/80 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 font-body text-[#1A1A1A]/80 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of marketing communications</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-[#800020] mb-4">Contact Us</h2>
            <p className="font-body text-[#1A1A1A]/80 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-[#F5F5F0] p-6 rounded-2xl">
              <p className="font-body text-[#1A1A1A]/80">
                <strong>KALABURAGI</strong><br />
                Shop No: 1-10/B KHUBA COMPLEX,<br />
                KHUBA PLOT GULBARGA KALABURAGI<br />
                KALABURAGI, KARNATAKA 585102<br />
                India<br /><br />
                <strong>Phone:</strong> +91 95358 34444<br />
                <strong>Email:</strong> arvindsutrave444@gmail.com
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl md:text-3xl text-[#800020] mb-4">Changes to This Policy</h2>
            <p className="font-body text-[#1A1A1A]/80 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>
        </motion.div>

        {/* Back to Home Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#800020] text-white font-body text-sm tracking-widest uppercase hover:bg-[#600018] transition-colors rounded-full"
            data-testid="back-home-btn"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
        </motion.div>
      </div>
    </div>
  );
};
